import { get, post, put } from '../lib'

export type UserSummary = {
  id: number
  username: string
  firstName: string
  lastName: string
}

export type VehicleSummary = {
  id: number
  name: string
  displayName: string
  vin: string
  make: string
  model: string
  year: number
  fleetId: number
}

export type DeviceSummary = {
  id: number
  serial: string
  name: string
  type: string
}

export type VehicleCertStatus =
  | 'requested'
  | 'needs_approval'
  | 'issued'
  | 'installed'
  | 'pending_revocation'
  | 'revoked'
  | 'expired'
  | 'failed'
  | 'cancelled'

export type VehicleCert = {
  id: number
  status: VehicleCertStatus
  tmpCert: string | null
  certExpiresAt: string | null
  requestedAt: string
  issuedAt: string | null
  deletedAt: string | null
  requestedBy: UserSummary
  issuedBy: UserSummary | null
  revokedBy: UserSummary | null
  vehicle: VehicleSummary
  device: DeviceSummary
}

export type VehicleCertQuery = {
  status?: string[]
  sort?: string
  order?: 'asc' | 'desc'
  offset?: number
  max?: number
  vehicleId?: number
  vehicleVin?: string
  deviceSerial?: string
  requestedByUsername?: string
  issuedByUsername?: string
  revokedByUsername?: string
  showAll?: boolean
  requestedAtAfter?: string
  requestedAtBefore?: string
  issuedAtAfter?: string
  issuedAtBefore?: string
  certExpiresAtAfter?: string
  certExpiresAtBefore?: string
  deletedAtAfter?: string
  deletedAtBefore?: string
}

export type VehicleCertPage = {
  data: VehicleCert[]
  paging: { max: number; offset: number; total: number }
}

export const getService29VehicleCerts = async (
  query: VehicleCertQuery = {}
): Promise<VehicleCertPage> => {
  const { status, showAll, ...scalar } = query

  const params = new URLSearchParams()
  Object.entries(scalar).forEach(([k, v]) => {
    if (v !== undefined) params.append(k, String(v))
  })
  if (showAll) params.append('showAll', 'true')
  status?.forEach((s) => params.append('status', s))

  const qs = params.toString()
  const res = await get<{ data: VehicleCert[]; headers: Headers }>({
    path: `/api/v1/vehicleCerts${qs ? '?' + qs : ''}`,
    includeHeaders: true
  })

  return {
    data: res.data,
    paging: {
      max: Number(res.headers.get('x-wivi-max')) || query.max || 50,
      offset: Number(res.headers.get('x-wivi-offset')) || query.offset || 0,
      total: Number(res.headers.get('x-wivi-total')) || 0
    }
  }
}

export const service29Login = async (
  certIds: number[] = []
): Promise<{ url: string }> => {
  const params = new URLSearchParams()
  certIds.forEach((id) => params.append('certIds', String(id)))
  return get<{ url: string }>({
    path: `/auth/service29/login?${params.toString()}`
  })
}

export const approveVehicleCerts = async (ids: number[] = []) =>
  post<{ ids: number[] }, VehicleCert[]>({
    path: '/api/v1/vehicleCerts/approve',
    body: {
      ids: ids
    }
  })

export const cancelVehicleCert = async (cert: VehicleCert) => {
  const status =
    cert.status === 'pending_revocation' ? 'installed' : 'cancelled'
  await put<Partial<VehicleCert>, VehicleCert>({
    path: `/api/v1/vehicleCerts/${cert.id}`,
    body: { status: status }
  })
}

export const revokeVehicleCerts = async (ids: number[] = []) =>
  post<{ ids: number[] }, VehicleCert[]>({
    path: '/api/v1/vehicleCerts/revoke',
    body: {
      ids: ids
    }
  })
